import React, { useEffect, useMemo, useState } from 'react';
import { getClientPickerOptions, getAgendaSdrs, createManualAppointment, type SdrOption } from '../../lib/refonteApi';

// Ajout d'un RDV à la main — filet quand l'automation (mail au démarrage, cold call) n'a pas créé le RDV.
// Statut initial = Booké. Réutilisable depuis n'importe quelle vue (production, RDV à confirmer…).
const AddRdvModal: React.FC<{
  defaultDate?: string;            // YYYY-MM-DD
  defaultSdrId?: string | null;
  defaultClientId?: string | null; // pré-sélection (ex. depuis le registre d'un client)
  onClose: () => void;
  onCreated: () => void;
}> = ({ defaultDate, defaultSdrId, defaultClientId, onClose, onCreated }) => {
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [sdrs, setSdrs] = useState<SdrOption[]>([]);
  const [clientId, setClientId] = useState<string | null>(defaultClientId ?? null);
  const [clientQ, setClientQ] = useState('');
  const [sdrId, setSdrId] = useState<string | null>(defaultSdrId ?? null);
  const [prospect, setProspect] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState(defaultDate || new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('10:00');
  const [icp, setIcp] = useState<boolean | null>(null);
  // Provenance OBLIGATOIRE (spec-provenance-rdv) : condition pour résorber les 4 619 sources null.
  const [source, setSource] = useState<'call' | 'email' | 'linkedin' | 'entrant'>('call');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { getClientPickerOptions().then(setClients).catch(() => {}); getAgendaSdrs().then(setSdrs).catch(() => {}); }, []);

  const selClient = clients.find(c => c.id === clientId) || null;
  const filteredClients = useMemo(() => {
    const t = clientQ.trim().toLowerCase();
    return clients.filter(c => !t || c.name.toLowerCase().includes(t)).slice(0, 40);
  }, [clients, clientQ]);

  const canSave = clientId && prospect.trim() && date && time && !saving;
  const submit = async () => {
    if (!canSave) return;
    setSaving(true); setErr(null);
    try {
      await createManualAppointment({
        clientId: clientId!, sdrId, source, prospectName: prospect.trim(),
        company: company.trim() || null, jobTitle: jobTitle.trim() || null,
        phone: phone.trim() || null, email: email.trim() || null,
        date, time, notes: notes.trim() || null, icpConformant: icp,
      });
      onCreated();
    } catch (e: any) { setErr(e?.message || "Échec de l'ajout"); setSaving(false); }
  };

  const field = 'w-full rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px]';
  const lbl = 'font-mono text-[9px] uppercase tracking-widest text-faint mb-1 block';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-panel border border-line rounded-card w-full max-w-lg p-5 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-[15px] font-bold">Ajouter un RDV à la main</h3>
          <button onClick={onClose} className="text-faint hover:text-ink text-[18px] leading-none">×</button>
        </div>
        <p className="text-dim text-[12px] mb-4">Pour un RDV que l'automation n'a pas créé (mail au démarrage, cold call…). Il sera enregistré en <b>Booké</b>.</p>

        {/* Client (recherche) */}
        <div className="mb-3">
          <label className={lbl}>Client *</label>
          {selClient ? (
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold flex-1">{selClient.name}</span>
              <button onClick={() => { setClientId(null); setClientQ(''); }} className="text-[11px] text-hook-rose hover:underline">changer</button>
            </div>
          ) : (
            <>
              <input value={clientQ} onChange={e => setClientQ(e.target.value)} placeholder="Chercher un client…" className={field} autoFocus />
              {clientQ.trim() && (
                <div className="max-h-36 overflow-y-auto flex flex-col gap-0.5 mt-1 border border-line rounded-md p-1">
                  {filteredClients.length === 0 ? <span className="text-[11px] text-faint px-1 py-1">Aucun client.</span>
                    : filteredClients.map(c => <button key={c.id} onClick={() => setClientId(c.id)} className="text-left text-[12.5px] px-2 py-1.5 rounded hover:bg-raise text-dim hover:text-ink truncate">{c.name}</button>)}
                </div>
              )}
            </>
          )}
        </div>

        {/* SDR */}
        <div className="mb-3">
          <label className={lbl}>SDR (qui a booké)</label>
          <select value={sdrId ?? ''} onChange={e => setSdrId(e.target.value || null)} className={field}>
            <option value="">— non précisé —</option>
            {sdrs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Prospect */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="col-span-2"><label className={lbl}>Prospect *</label><input value={prospect} onChange={e => setProspect(e.target.value)} placeholder="Prénom Nom" className={field} /></div>
          <div><label className={lbl}>Société</label><input value={company} onChange={e => setCompany(e.target.value)} className={field} /></div>
          <div><label className={lbl}>Poste</label><input value={jobTitle} onChange={e => setJobTitle(e.target.value)} className={field} /></div>
          <div><label className={lbl}>Téléphone</label><input value={phone} onChange={e => setPhone(e.target.value)} className={field} /></div>
          <div><label className={lbl}>Email</label><input value={email} onChange={e => setEmail(e.target.value)} className={field} /></div>
        </div>

        {/* Date / heure */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div><label className={lbl}>Date *</label><input type="date" value={date} onChange={e => setDate(e.target.value)} className={field} /></div>
          <div><label className={lbl}>Heure *</label><input type="time" value={time} onChange={e => setTime(e.target.value)} className={field} /></div>
        </div>

        {/* ICP */}
        <div className="mb-3">
          <label className={lbl}>Conforme à l'ICP ?</label>
          <div className="flex gap-1.5">
            {([['oui', true], ['non', false], ['—', null]] as const).map(([l, v]) => (
              <button key={l} onClick={() => setIcp(v)} className={`text-[12px] px-3 py-1 rounded-md border ${icp === v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{l}</button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-4">
          <label className={lbl}>Notes</label>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-dim">Provenance du RDV *</label>
            <div className="flex gap-1.5 mt-1 flex-wrap">
              {([['call', '☎️ Cold call'], ['email', '✉️ Mailing'], ['linkedin', '💼 LinkedIn'], ['entrant', '📥 Entrant']] as const).map(([v, l]) => (
                <button key={v} type="button" onClick={() => setSource(v)}
                  className={`text-[12px] px-2.5 py-1.5 rounded-lg border transition ${source === v ? 'border-ink bg-panel-2 font-semibold' : 'border-line text-dim hover:border-ink'}`}>{l}</button>
              ))}
            </div>
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={field} placeholder="Contexte, remarques…" />
        </div>

        {err && <p className="text-[12px] text-st-bad mb-2">{err}</p>}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-[13px] px-3 py-1.5 rounded-md border border-line-2 text-dim hover:border-ink">Annuler</button>
          <button onClick={submit} disabled={!canSave} className="text-[13px] px-4 py-1.5 rounded-md bg-hook-rose text-white font-semibold disabled:opacity-40">{saving ? 'Ajout…' : 'Ajouter le RDV'}</button>
        </div>
      </div>
    </div>
  );
};

export default AddRdvModal;
